import { isAfter, isValid } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { epochToFormatDate } from "../../app/common";
import { useAppDispatch } from "../../app/hooks";
import isEqual from "../../common/helpers/isEqual";
import useMessage from "../../hooks/useMessage";
import { SupplementDefinitions, VariationSpecificSettings } from "./supplementAPI";
import { fetchSupplementDefinitions } from "./supplementsSlice";

interface UseSupplementDefinitionsParams {
  tesId: string | null | undefined;
  date: number | null | undefined;
  tesSelections: [string, string][];
  areTesVariationsValid?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  setPeriodStartDateIsRequired?: Dispatch<SetStateAction<boolean | null>>;
  handleVariationSpecificSettings?: (data: VariationSpecificSettings) => void;
}

interface UseSupplementDefinitionsResult {
  supplementDefinitions: SupplementDefinitions;
  areDefinitionsLoading: boolean;
}

const useSupplementDefinitions = ({
  tesId,
  date,
  tesSelections,
  areTesVariationsValid,
  setIsLoading,
  setPeriodStartDateIsRequired,
  handleVariationSpecificSettings,
}: UseSupplementDefinitionsParams): UseSupplementDefinitionsResult => {
  const [supplementDefinitions, setSupplementDefinitions] =
    useState<SupplementDefinitions>({});
  const [areDefinitionsLoading, setAreDefinitionsLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();

  const prevInputsRef = useRef<{
    tesId: string | null | undefined;
    date: number | null | undefined;
    tesSelections: [string, string][] | null;
  }>({
    tesId: null,
    date: null,
    tesSelections: null,
  });

  const areInputsEqual = (prevInputs: any, nextInputs: any) => {
    return (
      isEqual(prevInputs.tesId, nextInputs.tesId) &&
      prevInputs.date === nextInputs.date &&
      isEqual(prevInputs.tesSelections, nextInputs.tesSelections)
    );
  };

  useEffect(() => {
    const currentInputs = { tesId, date, tesSelections };

    if (date && tesId && areTesVariationsValid) {
      const inputsChanged = !areInputsEqual(
        prevInputsRef.current,
        currentInputs,
      );
      const epochToDate = epochToFormatDate(date, "yyyy-MM-dd");

      if (inputsChanged && isValid(new Date(epochToDate))) {
        if (!isAfter(epochToDate, "2010-01-01")) {
          console.warn(
            "The provided date is before 2010-01-01. Supplement definitions will not be fetched.",
          );
          return;
        }

        const tesIdChanged =
          prevInputsRef.current.tesId !== null &&
          prevInputsRef.current.tesId !== tesId;

        if (tesIdChanged && tesSelections.length > 0) {
          return;
        }

        prevInputsRef.current = currentInputs;

        setAreDefinitionsLoading(true);
        dispatch(
          fetchSupplementDefinitions({
            tesId: tesId,
            date: epochToDate,
            tesSelections: tesSelections.filter(
              (selection) => selection[0] !== "" && selection[1] !== "",
            ),
          }),
        ).then((action: any) => {
          if (action.meta.requestStatus === "fulfilled") {
            const responseData = action.payload;
            const newSupplementDefinitions =
              responseData.supplements as SupplementDefinitions;
            setSupplementDefinitions({ ...newSupplementDefinitions });
            if (setPeriodStartDateIsRequired !== undefined) {
              setPeriodStartDateIsRequired(
                responseData.periodStartDateIsRequired,
              );
            }
            if (handleVariationSpecificSettings !== undefined) {
              handleVariationSpecificSettings(responseData);
            }
            setAreDefinitionsLoading(false);
          } else {
            action?.error?.message &&
              sendMessage(t(action.error.message), "error");
            console.error("Error fetching supplement definitions:");

            prevInputsRef.current = {
              tesId: null,
              date: null,
              tesSelections: null,
            };
            setAreDefinitionsLoading(false);
            setIsLoading && setIsLoading(false);
          }
        });
      }
    }
    // eslint-disable-next-line
  }, [tesId, date, tesSelections, dispatch]);

  return { supplementDefinitions, areDefinitionsLoading };
};

export default useSupplementDefinitions;
